/** Everything the page says about Ricky, in one place. Edit here, not in components. */

export const identity = {
  name: "Ricardo Escobar",
  nickname: "Ricky",
  role: "Founder & Software Engineer",
  company: "R1 Development",
  companyUrl: "https://r1.dev",
  location: "Houston, TX",
} as const

export interface WorkItem {
  readonly name: string
  readonly context: string
  readonly line: string
  readonly url?: string
}

/** Things I'm proud of, newest first. */
export const work: readonly WorkItem[] = [
  {
    name: "Qualified Roofs",
    context: "R1 Development · 2026",
    line: "Property-intelligence platform for a DFW roofing contractor. Fuses NOAA hail data, county appraisal rolls and city permit records into a reason-coded Roof Score, ranking ~1.8M single-family parcels by replacement likelihood.",
  },
  {
    name: "Belay",
    context: "R1 Development · 2026",
    line: "Booking, payments and subscriptions for small local service businesses. Customers answer job questions, get an instant quote, schedule, and pay up front through the operator's connected Stripe account.",
  },
  {
    name: "MLTI Intelligence",
    context: "Project",
    line: "Agentic AI due-diligence for multifamily underwriting. A computer-vision + OCR extraction pipeline reads offering memorandums, T12s and rent rolls into model-ready inputs.",
  },
  {
    name: "MaiFit",
    context: "Project · App Store",
    line: "Fitness coaching iOS app, built solo on React Native / Expo and shipped with paying users. AI-generated workout programs, logging, and progress tracking.",
    url: "https://apps.apple.com/us/app/maifit/id6739534912",
  },
]

export interface Experience {
  readonly org: string
  readonly title: string
  readonly dates: string
  readonly note?: string
  readonly points: readonly string[]
}

/** Newest first. */
export const experience: readonly Experience[] = [
  {
    org: "R1 Development",
    title: "Founder & Software Engineer",
    dates: "Mar 2026 – Present",
    note: "AI strategy & custom software · Houston, TX",
    points: [
      "Founded a firm bringing AI and custom software to small and mid-sized businesses — from process audits and AI opportunity mapping to designing, building and shipping the software itself.",
      "Client work so far includes Qualified Roofs and Belay, above.",
    ],
  },
  {
    org: "Paycom",
    title: "Software Developer IV",
    dates: "Apr 2023 – Mar 2026",
    note: "Promoted through four levels · Dallas, TX",
    points: [
      "Primary developer and steward of a wire-transaction dashboard processing $200B+ in payroll wire transfers — most of the frontend and two backend banking APIs, domestic and international.",
      "Improved hot-path query performance 7× with a custom caching layer, index-friendly queries, batching and projection-based EF Core reads; converted the banking API to a fully asynchronous architecture.",
      "Architected the React foundation for Paycom's Billing Hub — app architecture, routing, and a reusable base that multiple teams built on.",
      "Built event-driven pipelines on Apache Kafka over a .NET API; diagnosed and resolved a Kafka consumer defect that had already cost over $1M.",
    ],
  },
]

export const education = [
  {
    org: "Baylor University",
    detail:
      "B.B.A., magna cum laude — Management Information Systems; Entrepreneurship & Corporate Innovation",
    dates: "May 2022",
  },
  {
    org: "Harvard CS50x · CS50P · CS50W",
    detail: "Computer Science, Python and Web Programming certificates",
    dates: "",
  },
] as const

export const skills = [
  ["Languages", "TypeScript, JavaScript, C#, Python, SQL, C"],
  ["Frontend", "React, TanStack Start, Next.js, Redux Toolkit, i18n"],
  ["Mobile", "React Native, Expo, App Store"],
  ["Backend", ".NET, Node.js, Cloudflare Workers, Kafka, event-driven systems"],
  ["Data", "PostgreSQL, MySQL, SQLite, EF Core, Drizzle, query optimization"],
  ["AI", "LLM APIs, agent design, Claude Vision, agentic tooling"],
] as const

export const contact = [
  { label: "github", value: "github.com/rickyeescobar", href: "https://github.com/rickyeescobar" },
  {
    label: "linkedin",
    value: "linkedin.com/in/ricardo-escobar-",
    href: "https://www.linkedin.com/in/ricardo-escobar-/",
  },
  { label: "company", value: "r1.dev", href: "https://r1.dev" },
] as const
